import { supabase } from '../lib/supabase';
import HybridCreditService from './hybridCreditService';

/**
 * Service for managing assessment data and questionnaire progress
 */

/**
 * Check if user can start a new assessment (has credits or is premium)
 * @param {string} userId - The user ID
 * @param {boolean} isPremium - Whether user has premium subscription
 * @returns {Promise<{success: boolean, canStart: boolean, reason?: string, error?: string}>}
 */
export async function canUserStartAssessment(userId, isPremium = false) {
  try {
    // Premium users can always start assessments
    if (isPremium) {
      return { success: true, canStart: true, reason: 'premium' };
    }

    // Check if user has completed an assessment before
    const { data: existingAssessment, error: assessmentError } = await supabase
      .from('user_assessments')
      .select('id, is_completed')
      .eq('user_id', userId)
      .eq('is_completed', true)
      .single();

    if (assessmentError && assessmentError.code !== 'PGRST116') {
      throw new Error(`Failed to check existing assessment: ${assessmentError.message}`);
    }

    // If no completed assessment, user can take their free one
    if (!existingAssessment) {
      return { success: true, canStart: true, reason: 'free_first' };
    }

    // User has completed their free assessment, check for credits
    const creditCheck = await HybridCreditService.canTakeAssessment(userId, isPremium);
    return creditCheck;
  } catch (error) {
    console.error('Error checking assessment eligibility:', error);
    return { success: false, canStart: false, error: error.message };
  }
}

/**
 * Start a new assessment and deduct credits if needed
 * @param {string} userId - The user ID
 * @param {boolean} isPremium - Whether user has premium subscription
 * @returns {Promise<{success: boolean, assessmentId?: string, error?: string}>}
 */
export async function startNewAssessment(userId, isPremium = false) {
  try {
    // Check if user can start assessment
    const eligibilityCheck = await canUserStartAssessment(userId, isPremium);
    if (!eligibilityCheck.success) {
      return { success: false, error: eligibilityCheck.error };
    }

    if (!eligibilityCheck.canStart) {
      return { 
        success: false, 
        error: eligibilityCheck.reason === 'no_credits' 
          ? 'You need to purchase credits to take additional assessments. Upgrade to Premium or buy a credit pack.'
          : 'You cannot start a new assessment at this time.'
      };
    }

    // Note: Credit consumption is now moved to completeAssessment to ensure credits are only consumed when assessment is successfully completed

    // Create new assessment record
    const { data: newAssessment, error: createError } = await supabase
      .from('user_assessments')
      .insert({
        user_id: userId,
        is_completed: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (createError) {
      throw new Error(`Failed to create new assessment: ${createError.message}`);
    }

    // Credit usage is already recorded in the consumeCreditForAssessment call

    return { success: true, assessmentId: newAssessment.id };
  } catch (error) {
    console.error('Error starting new assessment:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Save assessment progress to database
 * @param {string} userId - The user ID
 * @param {Object} assessmentData - Assessment data including answers, attitudes, and setup
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function saveAssessmentProgress(userId, assessmentData) {
  try {
    const { answers, attitudes, assessmentSetup, currentQuestionIndex, isCompleted = false } = assessmentData;

    // Prepare the data for database storage
    const assessmentRecord = {
      user_id: userId,
      assessment_setup: assessmentSetup,
      answers: answers,
      attitudes: attitudes,
      current_question_index: currentQuestionIndex,
      is_completed: isCompleted,
      completed_at: isCompleted ? new Date().toISOString() : null,
      updated_at: new Date().toISOString()
    };

    // Check if assessment already exists for this user
    const { data: existingAssessment, error: fetchError } = await supabase
      .from('user_assessments')
      .select('id')
      .eq('user_id', userId)
      .single();

    let data, error;
    
    if (existingAssessment) {
      // Update existing assessment
      const result = await supabase
        .from('user_assessments')
        .update(assessmentRecord)
        .eq('id', existingAssessment.id)
        .select()
        .single();
      data = result.data;
      error = result.error;
    } else {
      // Create new assessment
      const result = await supabase
        .from('user_assessments')
        .insert({
          ...assessmentRecord,
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      data = result.data;
      error = result.error;
    }

    if (error) {
      throw new Error(`Failed to save assessment progress: ${error.message}`);
    }

    return {
      success: true,
      data
    };

  } catch (error) {
    console.error('Error saving assessment progress:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Load assessment progress from database
 * @param {string} userId - The user ID
 * @param {string} assessmentId - Optional specific assessment ID to load
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function loadAssessmentProgress(userId, assessmentId = null) {
  try {
    console.log('Loading assessment progress for user:', userId, 'assessmentId:', assessmentId);
    
    // First, let's check if the table exists by trying a simple query
    const { data: tableCheck, error: tableError } = await supabase
      .from('user_assessments')
      .select('id')
      .limit(1);

    console.log('Table check result:', { tableCheck, tableError });

    if (tableError) {
      console.error('Table access error:', tableError);
      // If table doesn't exist or RLS is blocking, return empty result
      return {
        success: true,
        data: null
      };
    }

    let query = supabase
      .from('user_assessments')
      .select('*')
      .eq('user_id', userId);

    // If specific assessment ID provided, load that one
    if (assessmentId) {
      query = query.eq('id', assessmentId);
    }

    const { data, error } = await query.single();

    console.log('Assessment query result:', { data, error });

    if (error && error.code !== 'PGRST116') { // PGRST116 is "not found" error
      console.error('Assessment query error:', error);
      // Don't throw error, just return empty result
      return {
        success: true,
        data: null
      };
    }

    return {
      success: true,
      data: data || null
    };

  } catch (error) {
    console.error('Error loading assessment progress:', error);
    return {
      success: true, // Don't fail, just return empty
      data: null
    };
  }
}

/**
 * Complete assessment and create career plan
 * @param {string} userId - The user ID
 * @param {Object} assessmentData - Complete assessment data
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function completeAssessment(userId, assessmentData) {
  try {
    const { answers, attitudes, assessmentSetup } = assessmentData;

    // First, save the completed assessment
    const saveResult = await saveAssessmentProgress(userId, {
      ...assessmentData,
      isCompleted: true
    });

    if (!saveResult.success) {
      throw new Error(saveResult.error);
    }

    // Now that assessment is successfully saved, consume credit if needed
    // Check if this is a free user who needs to spend credits (not their first free assessment)
    const { data: existingCompleted, error: existingError } = await supabase
      .from('user_assessments')
      .select('id')
      .eq('user_id', userId)
      .eq('is_completed', true)
      .neq('id', saveResult.data.id); // Exclude the current assessment

    if (!existingError && existingCompleted && existingCompleted.length > 0) {
      // User has completed assessments before, so they need to spend credits
      const spendResult = await HybridCreditService.consumeCreditForAssessment(userId, saveResult.data.id);
      
      if (!spendResult.success) {
        // If credit consumption fails, we should rollback the assessment completion
        console.error('Failed to consume credit for assessment:', spendResult.error);
        // Note: In a production system, you might want to rollback the assessment completion here
        // For now, we'll log the error but not fail the completion
      }
    }

    // Update user profile with occupation and seniority level
    try {
      // Find the occupation ID from the occupation title
      let occupationId = null;
      if (assessmentSetup.occupation) {
        const { data: occupationData, error: occupationError } = await supabase
          .from('occupations')
          .select('id')
          .ilike('title', assessmentSetup.occupation)
          .limit(1)
          .single();

        if (!occupationError && occupationData) {
          occupationId = occupationData.id;
        }
      }

      // Map seniority level to database enum
      const seniorityMapping = {
        'entry': 'entry',
        'junior': 'junior', 
        'mid': 'mid',
        'senior': 'senior',
        'executive': 'executive',
        'freelance': 'freelance'
      };
      
      const seniorityLevel = seniorityMapping[assessmentSetup.seniority?.toLowerCase()] || 'mid';

      // Update user profile
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({
          occupation_id: occupationId,
          seniority_level: seniorityLevel,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (profileError) {
        console.warn('Failed to update user profile with occupation data:', profileError);
        // Don't throw error here, just log it as the assessment can still be completed
      } else {
        console.log('Successfully updated user profile with occupation and seniority level');
      }
    } catch (profileUpdateError) {
      console.warn('Error updating user profile:', profileUpdateError);
      // Don't throw error here, just log it as the assessment can still be completed
    }

    // Create a career plan record (this will be populated by AI later)
    const careerPlanData = {
      user_id: userId,
      occupation: assessmentSetup.occupation,
      seniority_level: assessmentSetup.seniority,
      industry: assessmentSetup.industry,
      location_data: {
        currentZipCode: assessmentSetup.currentZipCode,
        locationPreference: assessmentSetup.locationPreference,
        targetLocation: assessmentSetup.targetLocation
      },
      assessment_data: {
        answers,
        attitudes,
        assessmentSetup
      },
      plan_data: {}, // Will be populated by AI
      is_active: true
    };

    const { data: planData, error: planError } = await supabase
      .from('career_plans')
      .insert([careerPlanData])
      .select()
      .single();

    if (planError) {
      throw new Error(`Failed to create career plan: ${planError.message}`);
    }

    return {
      success: true,
      data: {
        assessment: saveResult.data,
        careerPlan: planData
      }
    };

  } catch (error) {
    console.error('Error completing assessment:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Get user's assessment history
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export async function getAssessmentHistory(userId) {
  try {
    const { data, error } = await supabase
      .from('user_assessments')
      .select(`
        *,
        career_plans (*)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch assessment history: ${error.message}`);
    }

    return {
      success: true,
      data: data || []
    };

  } catch (error) {
    console.error('Error fetching assessment history:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Delete assessment data (for retakes)
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deleteAssessmentData(userId) {
  try {
    // Delete assessment progress
    const { error: assessmentError } = await supabase
      .from('user_assessments')
      .delete()
      .eq('user_id', userId);

    if (assessmentError) {
      throw new Error(`Failed to delete assessment data: ${assessmentError.message}`);
    }

    // Delete associated career plans
    const { error: planError } = await supabase
      .from('career_plans')
      .delete()
      .eq('user_id', userId);

    if (planError) {
      throw new Error(`Failed to delete career plans: ${planError.message}`);
    }

    return {
      success: true
    };

  } catch (error) {
    console.error('Error deleting assessment data:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

